import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { deleteLead, getLeads, updateLeadStatus } from "../services/crmApi";
import { routePaths } from "../constants/routes";
import { useAuth } from "../hooks/useAuth";
import "../style/leadCRM.css";

const STATUS_OPTIONS = [
  "new",
  "contacted",
  "demo scheduled",
  "demo attended",
  "interested",
  "enrolled",
  "lost",
];

const TYPE_OPTIONS = ["all", "demo", "newsletter", "course", "general"];

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function getLeadType(lead) {
  if (lead.enquiryType) return lead.enquiryType.toLowerCase();
  if (lead.course || lead.courseInterest) return "course";
  return "general";
}

function getWhatsAppPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  return digits;
}

function getLeadName(lead) {
  return lead.name?.trim() || "there";
}

function getCourseLabel(lead) {
  return lead.courseInterest || lead.course || "software testing training";
}

function buildFollowUpMessage(lead) {
  const name = getLeadName(lead);
  const course = getCourseLabel(lead);
  const experience = lead.experienceLevel ? `\nExperience shared: ${lead.experienceLevel}` : "";
  const mode = lead.mode ? `\nPreferred mode: ${lead.mode}` : "";

  return `Hi ${name},

Thank you for contacting QA Coder.

We received your enquiry for ${course}.${experience}${mode}

We will connect with you soon and share complete details.

Here is what we will help you with:
1. Course roadmap and learning path
2. Demo class timing
3. Fees, batch options, and duration
4. Guidance based on your current experience
5. Next steps for enrollment

Please reply with your preferred time for a call or demo session.

Regards,
QA Coder Team`;
}

function openWhatsApp(lead) {
  const phone = getWhatsAppPhone(lead.phone);
  if (!phone) {
    alert("Phone number not available.");
    return;
  }

  const text = buildFollowUpMessage(lead);
  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
}

function openEmail(lead) {
  if (!lead.email) {
    alert("Email not available.");
    return;
  }

  const course = getCourseLabel(lead);
  const subject = `QA Coder: Details for ${course}`;
  const body = buildFollowUpMessage(lead);

  window.location.href = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function matchesSearch(lead, search) {
  if (!search.trim()) return true;
  const q = search.toLowerCase();

  return [
    lead.name,
    lead.email,
    lead.phone,
    lead.course,
    lead.courseInterest,
    lead.message,
    lead.source,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(q);
}

function StatCard({ title, value, subtext, tone }) {
  return (
    <article className={`crm-stat-card ${tone || ""}`}>
      <span>{title}</span>
      <strong>{value}</strong>
      {subtext && <small>{subtext}</small>}
    </article>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="crm-detail-item">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function CrmSidebar({ role }) {
  const dashboardPath = role === "admin" ? routePaths.admin : routePaths.trainer;
  const dashboardLabel = role === "admin" ? "Admin Dashboard" : "Trainer Dashboard";

  return (
    <aside className="crm-sidebar">
      <div className="crm-sidebar-brand">
        <strong>QA Coder</strong>
        <span>{role === "admin" ? "Admin workspace" : "Trainer workspace"}</span>
      </div>

      <nav className="crm-sidebar-nav" aria-label="CRM dashboard navigation">
        <p>Workspace</p>
        <NavLink to={dashboardPath}>
          {dashboardLabel}
        </NavLink>
        {["admin", "trainer"].includes(role) && (
          <NavLink to={routePaths.leadCrm}>
            Lead CRM
          </NavLink>
        )}
      </nav>
    </aside>
  );
}

function LeadDetailsModal({ selectedLead, closeModal, onSave, saving }) {
  const [leadStatus, setLeadStatus] = useState("new");
  const [internalNote, setInternalNote] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");

  useEffect(() => {
    if (selectedLead) {
      setLeadStatus(selectedLead.leadStatus || "new");
      setInternalNote(selectedLead.internalNote || "");
      setFollowUpDate(selectedLead.followUpDate || "");
    }
  }, [selectedLead]);

  if (!selectedLead) return null;

  return (
    <div className="crm-modal-overlay" role="presentation" onMouseDown={closeModal}>
      <section
        className="crm-modal"
        aria-modal="true"
        aria-labelledby="lead-details-title"
        role="dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="crm-modal-header">
          <div>
            <span>Lead details</span>
            <h2 id="lead-details-title">{selectedLead.name || "Lead Details"}</h2>
            <p>{selectedLead.email || "No email provided"}</p>
          </div>
          <button type="button" className="crm-icon-button" onClick={closeModal} aria-label="Close lead details">
            x
          </button>
        </header>

        <div className="crm-detail-grid">
          <DetailItem label="Phone" value={selectedLead.phone} />
          <DetailItem label="Enquiry Type" value={selectedLead.enquiryType} />
          <DetailItem label="Course" value={selectedLead.course || selectedLead.courseInterest} />
          <DetailItem label="Experience" value={selectedLead.experienceLevel} />
          <DetailItem label="Mode" value={selectedLead.mode} />
          <DetailItem label="Source" value={selectedLead.source} />
          <DetailItem label="Landing Page" value={selectedLead.landingPage} />
          <DetailItem label="Referrer" value={selectedLead.referrer} />
          <DetailItem label="Created At" value={formatDate(selectedLead.createdAt)} />
          <DetailItem label="Updated At" value={formatDate(selectedLead.updatedAt)} />
        </div>

        <div className="crm-form-grid">
          <label>
            <span>Lead Status</span>
            <select value={leadStatus} onChange={(event) => setLeadStatus(event.target.value)}>
              {STATUS_OPTIONS.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {statusOption}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Follow-up Date</span>
            <input
              type="date"
              value={followUpDate || ""}
              onChange={(event) => setFollowUpDate(event.target.value)}
            />
          </label>
        </div>

        <label className="crm-field">
          <span>Internal Note</span>
          <textarea
            rows="5"
            value={internalNote}
            onChange={(event) => setInternalNote(event.target.value)}
            placeholder="Write call notes, learner goals, objections, and next steps."
          />
        </label>

        <div className="crm-message-panel">
          <span>Message</span>
          <p>{selectedLead.message || "-"}</p>
        </div>

        <footer className="crm-modal-actions">
          <button type="button" className="crm-btn secondary" onClick={closeModal}>
            Cancel
          </button>
          <button
            type="button"
            className="crm-btn primary"
            disabled={saving}
            onClick={() =>
              onSave(selectedLead.id, {
                leadStatus,
                internalNote,
                followUpDate,
              })
            }
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function LeadCRM() {
  const { role } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [enquiryType, setEnquiryType] = useState("all");

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setNotice("");

      const params = {};
      if (status !== "all") params.status = status;
      if (enquiryType !== "all") params.enquiryType = enquiryType;
      if (search.trim()) params.search = search.trim();

      const data = await getLeads(params);
      setLeads(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
      setNotice("Unable to load CRM leads. Confirm the Spring Boot CRM API is running on port 8081.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // Search runs on explicit button click so typing does not hammer the API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, enquiryType]);

  const stats = useMemo(() => {
    const total = leads.length;
    const newLeads = leads.filter((lead) => lead.leadStatus === "new").length;
    const demoLeads = leads.filter((lead) => getLeadType(lead) === "demo").length;
    const enrolledLeads = leads.filter((lead) => lead.leadStatus === "enrolled").length;

    return { total, newLeads, demoLeads, enrolledLeads };
  }, [leads]);

  const filteredLeads = useMemo(() => leads.filter((lead) => matchesSearch(lead, search)), [leads, search]);

  const handleStatusInlineChange = async (id, nextStatus) => {
    try {
      await updateLeadStatus(id, { leadStatus: nextStatus });
      await fetchLeads();
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("Failed to update status.");
    }
  };

  const handleSaveLead = async (id, payload) => {
    try {
      setSaving(true);
      await updateLeadStatus(id, payload);
      setSelectedLead(null);
      await fetchLeads();
    } catch (error) {
      console.error("Failed to save lead:", error);
      alert("Failed to save lead.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this lead?");
    if (!confirmDelete) return;

    try {
      await deleteLead(id);
      await fetchLeads();
    } catch (error) {
      console.error("Failed to delete lead:", error);
      alert("Failed to delete lead.");
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchLeads();
  };

  return (
    <section className="crm-page">
      <div className="crm-dashboard-layout">
        <CrmSidebar role={role} />

        <div className="crm-main-panel">
          <header className="crm-header">
            <div>
              <span>QA Coder CRM</span>
              <h1>Lead CRM Dashboard</h1>
              <p>Manage enquiries, follow-ups, and learner conversions in one place.</p>
            </div>
            <button type="button" className="crm-btn secondary" onClick={fetchLeads}>
              Refresh Leads
            </button>
          </header>

          {notice && <div className="crm-alert">{notice}</div>}

          <div className="crm-stats-grid">
            <StatCard title="Total Leads" value={stats.total} subtext="Current CRM queue" tone="blue" />
            <StatCard title="New Leads" value={stats.newLeads} subtext="Need first response" tone="green" />
            <StatCard title="Demo Leads" value={stats.demoLeads} subtext="Trial class interest" tone="teal" />
            <StatCard title="Enrolled" value={stats.enrolledLeads} subtext="Converted learners" tone="orange" />
          </div>

          <form className="crm-filter-panel" onSubmit={handleSearchSubmit}>
            <input
              type="search"
              placeholder="Search by name, email, phone, course, message..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search leads"
            />

            <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {statusOption}
                </option>
              ))}
            </select>

            <select
              value={enquiryType}
              onChange={(event) => setEnquiryType(event.target.value)}
              aria-label="Filter by enquiry type"
            >
              {TYPE_OPTIONS.map((typeOption) => (
                <option key={typeOption} value={typeOption}>
                  {typeOption === "all" ? "All Types" : typeOption}
                </option>
              ))}
            </select>

            <button type="submit" className="crm-btn primary">
              Search
            </button>
          </form>

          <div className="crm-table-panel">
            {loading ? (
              <div className="crm-empty-state">Loading leads...</div>
            ) : filteredLeads.length === 0 ? (
              <div className="crm-empty-state">No leads found.</div>
            ) : (
              <div className="crm-table-scroll">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Type</th>
                      <th>Course / Interest</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id}>
                        <td>
                          <strong>{lead.name || "-"}</strong>
                          <small>{lead.experienceLevel || "-"}</small>
                        </td>
                        <td>
                          <span>{lead.phone || "-"}</span>
                          <small>{lead.email || "-"}</small>
                        </td>
                        <td>
                          <span className="crm-type-badge">{getLeadType(lead)}</span>
                        </td>
                        <td>{lead.course || lead.courseInterest || "-"}</td>
                        <td>
                          <span>{lead.source || "-"}</span>
                          <small>{lead.sourcePath || "-"}</small>
                        </td>
                        <td>
                          <select
                            className="crm-status-select"
                            value={lead.leadStatus || "new"}
                            onChange={(event) => handleStatusInlineChange(lead.id, event.target.value)}
                          >
                            {STATUS_OPTIONS.map((statusOption) => (
                              <option key={statusOption} value={statusOption}>
                                {statusOption}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>{formatDate(lead.createdAt)}</td>
                        <td>
                          <div className="crm-action-wrap">
                            <button type="button" className="crm-action whatsapp" onClick={() => openWhatsApp(lead)}>
                              WhatsApp
                            </button>
                            <button type="button" className="crm-action email" onClick={() => openEmail(lead)}>
                              Email
                            </button>
                            <button type="button" className="crm-action details" onClick={() => setSelectedLead(lead)}>
                              Details
                            </button>
                            <button type="button" className="crm-action delete" onClick={() => handleDeleteLead(lead.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <LeadDetailsModal
        selectedLead={selectedLead}
        closeModal={() => setSelectedLead(null)}
        onSave={handleSaveLead}
        saving={saving}
      />
    </section>
  );
}
